// "I'm not a robot" on the enquiry forms (templates: "bot-check"). The
// form carries a signed challenge "salt.expiry.bits.signature"; this finds
// a number n such that sha256("salt:n") starts with `bits` zero bits and
// puts it in the form, which the server checks (internal/site/captcha.go).
// Work starts quietly when the visitor first focuses the form, so ticking
// the box is usually instant.

// SHA-256 round constants and initial state, derived from the primes the
// way the standard defines them.
const K = new Int32Array(64);
const H0 = new Int32Array(8);
{
  const frac = (x) => ((x - Math.floor(x)) * 4294967296) | 0;
  for (let n = 2, found = 0; found < 64; n++) {
    let prime = true;
    for (let d = 2; d * d <= n; d++) if (n % d === 0) { prime = false; break; }
    if (!prime) continue;
    if (found < 8) H0[found] = frac(n ** 0.5);
    K[found++] = frac(n ** (1 / 3));
  }
}
const W = new Int32Array(64);
const rotr = (x, n) => (x >>> n) | (x << (32 - n));

/** The first 32 bits of sha256(text), for ASCII text under 56 bytes (one block). */
export function sha256Word(text) {
  W.fill(0);
  for (let i = 0; i < text.length; i++) W[i >> 2] |= text.charCodeAt(i) << (24 - (i % 4) * 8);
  W[text.length >> 2] |= 0x80 << (24 - (text.length % 4) * 8);
  W[15] = text.length * 8;
  for (let i = 16; i < 64; i++) {
    const a = W[i - 15], b = W[i - 2];
    W[i] = (W[i - 16] + (rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3)) + W[i - 7] + (rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10))) | 0;
  }
  let [a, b, c, d, e, f, g, h] = H0;
  for (let i = 0; i < 64; i++) {
    const t1 = (h + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + W[i]) | 0;
    const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
    h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
  }
  return (H0[0] + a) | 0;
}

/** Finds the puzzle's answer in small batches, so the page stays responsive. */
async function solve(salt, bits) {
  for (let n = 0; ; ) {
    for (const end = n + 20000; n < end; n++) {
      if (Math.clz32(sha256Word(salt + ':' + n)) >= bits) return String(n);
    }
    await new Promise((resolve) => setTimeout(resolve));
  }
}

export function initCaptcha(root) {
  for (const box of root.querySelectorAll?.('[data-captcha]:not([data-captcha-init])') ?? []) {
    box.dataset.captchaInit = '';
    const form = box.closest('form');
    const check = box.querySelector('[data-captcha-check]');
    const nonce = box.querySelector('[data-captcha-nonce]');
    const state = box.querySelector('[data-captcha-state]');
    const [salt, , bits] = (form?.querySelector('input[name="captcha"]')?.value ?? '').split('.');
    if (!form || !check || !salt) continue;
    box.hidden = false;
    check.required = true;
    let job;
    const start = () => (job ??= solve(salt, Number(bits)).then((n) => { nonce.value = n; }));
    form.addEventListener('focusin', start, { once: true });
    check.addEventListener('change', async () => {
      if (!check.checked) {
        box.dataset.state = '';
        state.textContent = '';
        return;
      }
      if (!nonce.value) {
        box.dataset.state = 'working';
        state.textContent = 'Checking…';
        // Hold the form back until the check is done.
        check.setCustomValidity('Wait a moment while we check this form.');
        await start();
        check.setCustomValidity('');
      }
      box.dataset.state = 'done';
      state.textContent = 'Verified';
    });
  }
}
